import { RiCompass4Line, Stack } from 'component-library';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';

import { ConservationExplainerWell, NCCardSectionHeader } from '@/components/NCCard/NCCard';
import { useGetConservationBenchmarks } from '@/pages/shared/projects/pages/project/pages/conservation/BU/hooks/useGetConservationBenchmarks';
import { useGetExplainerFallbackText } from '@/pages/shared/projects/pages/project/pages/conservation/BU/hooks/useGetExplainerFallbackText';
import { useGetTrendLabel } from '@/pages/shared/projects/pages/project/pages/conservation/BU/hooks/useGetTrendLabel';
import { getBenchmarkForConservationFact } from '@/pages/shared/projects/pages/project/pages/conservation/BU/utils/getBenchmarkForConservationFact';
import {
  FactCapsule,
  FactCapsuleTrend,
} from '@/pages/shared/projects/pages/project/pages/conservation/shared/components/FactCapsule';
import { useFact } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useFact';
import { useLatestAnalysisDatestamp } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useLatestAnalysisDatestamp';

const FACTS = {
  baseline: 'r1_conservation_ecological_integrity_baseline',
  latest: 'r1_conservation_ecological_integrity',
  change: 'r1_conservation_ecological_integrity_change_percentage',
} as const;

export const StatsTile = () => {
  const { t } = useTranslation();

  const currentYear = useFact<number>('r1_conservation_current_year').value;
  const baselineYear = useFact<number>('r1_conservation_ecological_integrity_baseline_year').value;
  const ecologicalIntegrityBaseline = useFact<number>(FACTS.baseline);
  const ecologicalIntegrityAverage = useFact<number>(FACTS.latest);
  const ecologicalIntegrityPctChange = useFact<number>(FACTS.change);

  const benchmarks = useGetConservationBenchmarks();
  const projectBenchmark = getBenchmarkForConservationFact({
    fact: ecologicalIntegrityAverage,
    benchmarks,
  });

  const trendLabel = useGetTrendLabel({
    trendChangeFact: FACTS.change,
  });

  const customExplainerText = useFact<string>('r1_conservation_ecological_integrity_historic_explainer');

  const { benchmarkText, trendText } = useGetExplainerFallbackText({
    benchmark: {
      label: projectBenchmark,
      value: ecologicalIntegrityAverage.value,
    },
    trend: {
      label: trendLabel,
      value: ecologicalIntegrityPctChange.value,
    },
    indicator: 'ecologicalIntegrity',
  });

  const fallbackExplainerText =
    benchmarkText && trendText ? `${benchmarkText}\n\n${trendText}` : benchmarkText || trendText || null;

  const latestAnalysisDatestamp = useLatestAnalysisDatestamp(
    ecologicalIntegrityAverage?.measured_at ? { facts: ecologicalIntegrityAverage } : undefined,
  );

  return (
    <Stack spacing={10} className='px-6' data-testid='ecological-integrity-stats-tile'>
      <Stack spacing={6}>
        <NCCardSectionHeader
          title={t('shared.ncaDetail.ecologicalIntegrity.historicalDataBU.explainers.ecologicalIntegrity.title')}
          popover={{
            title: t('shared.ncaDetail.ecologicalIntegrity.historicalDataBU.explainers.ecologicalIntegrity.title'),
            body: t('shared.ncaDetail.ecologicalIntegrity.historicalDataBU.explainers.ecologicalIntegrity.body'),
          }}
          supplementaryInfo={latestAnalysisDatestamp}
        />
        <Stack spacing={16} className='flex-wrap md:flex-row'>
          <FactCapsule
            label={t('shared.ncaDetail.ecologicalIntegrity.historicalDataBU.explainers.baseline.title')}
            fact={ecologicalIntegrityBaseline}
            capsuleIconProps={{ icon: <RiCompass4Line /> }}
            infoPopoverProps={{
              title: t('shared.ncaDetail.ecologicalIntegrity.historicalDataBU.explainers.baseline.title'),
              body: t('shared.ncaDetail.ecologicalIntegrity.historicalDataBU.explainers.baseline.body', {
                year: baselineYear,
              }),
              popoverTriggerProps: {
                'data-testid': 'ecological-integrity-baseline-value-popover',
              },
            }}
          />
          <FactCapsule
            label={t('shared.ncaDetail.ecologicalIntegrity.historicalDataBU.explainers.averageValue.title', {
              year: currentYear,
            })}
            fact={ecologicalIntegrityAverage}
            capsuleIconProps={{ icon: <RiCompass4Line /> }}
            infoPopoverProps={{
              title: t('shared.ncaDetail.ecologicalIntegrity.historicalDataBU.explainers.averageValue.title', {
                year: currentYear,
              }),
              body: t('shared.ncaDetail.ecologicalIntegrity.historicalDataBU.explainers.averageValue.body', {
                year: currentYear,
              }),
              popoverTriggerProps: {
                'data-testid': 'ecological-integrity-average-value-popover',
              },
            }}
            trend={<FactCapsuleTrend trend={ecologicalIntegrityPctChange} />}
          />
        </Stack>
      </Stack>
      <ConservationExplainerWell
        benchmark={projectBenchmark}
        trendLabel={trendLabel}
        title={t('global.whatDoesThisMean')}
        body={
          customExplainerText?.value ? (
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(customExplainerText.value),
              }}
            />
          ) : (
            <span className='whitespace-pre-wrap'>{fallbackExplainerText}</span>
          )
        }
      />
    </Stack>
  );
};
